import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectSelectedRoom } from "../../../../store/room/room-selectors";
import {
  CleaningConfigurationLevel,
  DEFAULT_MIN_OBSTACLE_HEIGHT,
  getCleaningModeOptionText,
  MinObstacleHeightOption,
} from "../../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import {
  selectGlobalMapRulesConfiguration,
  selectParamListRestState,
} from "../../../../store/robot-configurations/robot-configurations-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import CustomAlert from "../../../../components/Feedback/CustomAlert";
import CustomLoader from "../../../../components/Feedback/CustomLoader";
import RuleZone from "../../../../interfaces/robot/rooms/rule-zone";
import { MapRulesConfiguration } from "../../../../interfaces/robot/cleaning-configurations/map-rules-configuration";
import { getEnumSelectOptions } from "../../../../utils/enums";
import { DistanceUnit } from "../../../../enums/configuration/units";
import { selectDistanceUnit } from "../../../../store/app-configurations/app-configurations-selectors";
import FrontLightsMode from "../../../../interfaces/robot/cleaning-configurations/front-lights-mode";
import VisibilityConfiguration from "../../../Configuration/CleaningConfiguration/global/VisibilityConfiguration";
import OptionSwitchInput from "../../../Inputs/OptionSwitchInput";
import { CustomTuneFiltersIcon, InheritIcon } from "../../../../utils/icons/icons";

export const BEEPER_DEFAULT_VALUE = false;
export const WARNING_LIGHT_DEFAULT_VALUE = false;
export const FRONT_LIGHTS_DEFAULT_VALUE = FrontLightsMode.Off;

const SelectedZoneFormRuleZoneDetails: React.FC<{
  zone: RuleZone;
}> = ({ zone }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getParamList, patchRuleZone } = useSharedActions();

  const room = useSharedSelector((state) => selectSelectedRoom(state));
  const globalMapRulesConfiguration = useSharedSelector((state) => selectGlobalMapRulesConfiguration(state));
  const paramListRest = useSharedSelector((state) => selectParamListRestState(state));
  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));

  useEffect(() => {
    if (paramListRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getParamList());
    }
  }, [dispatch, getParamList, paramListRest.getStatus]);

  if ([HttpFetchStatus.Idle, HttpFetchStatus.Loading].includes(paramListRest.getStatus))
    return <CustomLoader loadingMessage={t("loading-global-cleaning-configuration")} />;
  if (globalMapRulesConfiguration == null)
    return <CustomAlert header={t("error-loading-global-cleaning-configuration")} />;

  const updateMapRulesConfigurationHandler = (data: Partial<MapRulesConfiguration>) => {
    dispatch(
      patchRuleZone({
        id: zone.id,
        data: {
          mapRulesConfiguration: {
            ...zone.mapRulesConfiguration,
            ...data,
          },
        },
      }),
    );
  };

  return (
    <Grid item xs={12} gap={1}>
      <Stack spacing={2}>
        <VisibilityConfiguration
          configurationLevel={CleaningConfigurationLevel.Zone}
          onMapRulesConfigurationChange={updateMapRulesConfigurationHandler}
          globalMapRulesConfiguration={globalMapRulesConfiguration}
          roomMapRulesConfiguration={room?.mapRulesConfiguration}
          zoneMapRulesConfiguration={zone.mapRulesConfiguration}
        />

        <OptionSwitchInput
          label={t("cleaning-configuration-min-obstacle-height")}
          options={getEnumSelectOptions(MinObstacleHeightOption).map((option) => ({
            label: t(`cleaning-configuration-min-obstacle-height-${option.name}`),
            value: option.value,
            tooltip: t(option.name + "-description" + (distanceUnit === DistanceUnit.METRIC ? "-metric" : "-imperial")),
          }))}
          value={zone.mapRulesConfiguration.minObstacleHeightM}
          onChange={(v: number) => updateMapRulesConfigurationHandler({ minObstacleHeightM: v })}
          subButton={
            zone.mapRulesConfiguration != null
              ? {
                  label:
                    zone.mapRulesConfiguration.minObstacleHeightM == null
                      ? t("button-override-default-configs")
                      : getCleaningModeOptionText(
                          null,
                          CleaningConfigurationLevel.Zone,
                          room?.mapRulesConfiguration.minObstacleHeightM,
                          globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                        ),
                  startIcon:
                    zone.mapRulesConfiguration.minObstacleHeightM == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                  onClick: () =>
                    updateMapRulesConfigurationHandler({
                      minObstacleHeightM:
                        zone.mapRulesConfiguration.minObstacleHeightM == null
                          ? (room?.mapRulesConfiguration?.minObstacleHeightM ??
                            globalMapRulesConfiguration?.minObstacleHeightM ??
                            DEFAULT_MIN_OBSTACLE_HEIGHT)
                          : null,
                    }),
                }
              : room?.mapRulesConfiguration != null
                ? {
                    label:
                      room?.mapRulesConfiguration.minObstacleHeightM == null
                        ? t("button-override-default-configs")
                        : getCleaningModeOptionText(
                            null,
                            CleaningConfigurationLevel.Zone,
                            room?.mapRulesConfiguration?.minObstacleHeightM,
                            globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                          ),
                    startIcon:
                      room?.mapRulesConfiguration.minObstacleHeightM == null ? (
                        <CustomTuneFiltersIcon />
                      ) : (
                        <InheritIcon />
                      ),
                    onClick: () =>
                      updateMapRulesConfigurationHandler({
                        minObstacleHeightM:
                          room?.mapRulesConfiguration.minObstacleHeightM == null
                            ? (globalMapRulesConfiguration?.minObstacleHeightM ?? DEFAULT_MIN_OBSTACLE_HEIGHT)
                            : null,
                      }),
                  }
                : undefined
          }
          buttonOverrideText={
            zone.mapRulesConfiguration.minObstacleHeightM == null
              ? getCleaningModeOptionText(
                  null,
                  CleaningConfigurationLevel.Zone,
                  room?.mapRulesConfiguration.minObstacleHeightM,
                  globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                )
              : undefined
          }
        />
      </Stack>
    </Grid>
  );
};

export default SelectedZoneFormRuleZoneDetails;
